import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, Plus, Edit2, Trash2, X, Loader2, FileText, Download, ChevronDown } from 'lucide-react';
import api from '../lib/api';

const emptyJob = {
    title: '',
    company: '',
    location: '',
    job_type: 'Full-time',
    experience_level: 'Entry',
    salary_range: '',
    course_tag: 'All',
    description: '',
    requirements: '',
    skills_required: '',
    application_deadline: '',
    apply_link: '',
    company_logo: '',
    is_active: true,
};

const STATUS_ORDER = ['submitted', 'reviewed', 'shortlisted', 'rejected'];
const STATUS_STYLE = {
    submitted: 'bg-blue-100 text-blue-700',
    reviewed: 'bg-purple-100 text-purple-700',
    shortlisted: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
};

export default function AdminJobs() {
    const [tab, setTab] = useState('jobs');
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editJob, setEditJob] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [jr, ar] = await Promise.all([
                api.get('/admin/jobs'),
                api.get('/admin/applications'),
            ]);
            setJobs(jr.data || []);
            setApplications(ar.data || []);
        } catch (_) { /* ignore */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const removeJob = async (j) => {
        if (!confirm(`Delete "${j.title}" and its applications?`)) return; // eslint-disable-line no-alert
        await api.delete(`/admin/jobs/${j.id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="admin-jobs-page">
            <header>
                <div className="inline-flex items-center gap-2 rounded-full bg-[#FEF2F2] px-3 py-1 text-xs font-semibold text-[#E52C2C]">
                    <Briefcase className="h-3.5 w-3.5" /> JOB PORTAL MANAGEMENT
                </div>
                <h1 className="mt-2 text-2xl md:text-3xl font-extrabold text-[#111827]">Manage Jobs & Applications</h1>
            </header>

            <div className="flex items-center gap-1 border-b border-[#E5E7EB]">
                <TabButton active={tab === 'jobs'} onClick={() => setTab('jobs')} testId="tab-jobs">
                    Jobs ({jobs.length})
                </TabButton>
                <TabButton active={tab === 'applications'} onClick={() => setTab('applications')} testId="tab-applications">
                    All Applications ({applications.length})
                </TabButton>
            </div>

            {loading ? (
                <div className="brand-card p-10 flex items-center justify-center gap-3 text-[#6B7280]">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
            ) : tab === 'jobs' ? (
                <JobsTab
                    jobs={jobs}
                    applications={applications}
                    onAdd={() => setShowAdd(true)}
                    onEdit={(j) => setEditJob(j)}
                    onDelete={removeJob}
                />
            ) : (
                <ApplicationsTab applications={applications} reload={load} />
            )}

            <AnimatePresence>
                {showAdd && (
                    <JobFormModal
                        initial={emptyJob}
                        title="Post New Job"
                        onClose={() => setShowAdd(false)}
                        onSubmit={async (payload) => { await api.post('/admin/jobs', payload); setShowAdd(false); load(); }}
                    />
                )}
                {editJob && (
                    <JobFormModal
                        initial={{
                            ...editJob,
                            requirements: (editJob.requirements || []).join('\n'),
                            skills_required: (editJob.skills_required || []).join(', '),
                        }}
                        title="Edit Job"
                        onClose={() => setEditJob(null)}
                        onSubmit={async (payload) => { await api.put(`/admin/jobs/${editJob.id}`, payload); setEditJob(null); load(); }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function TabButton({ children, active, onClick, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`relative px-4 py-2 text-sm font-semibold transition ${
                active ? 'text-[#1C3A94]' : 'text-[#6B7280] hover:text-[#111827]'
            }`}
        >
            {children}
            {active && (
                <motion.div
                    layoutId="tab-indicator"
                    className="absolute inset-x-0 -bottom-px h-0.5 bg-[#1C3A94]"
                />
            )}
        </button>
    );
}

function JobsTab({ jobs, applications, onAdd, onEdit, onDelete }) {
    const countByJob = (jid) => applications.filter((a) => a.job_id === jid).length;
    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <button data-testid="post-job-btn" onClick={onAdd} className="brand-btn-primary gap-2">
                    <Plus className="h-4 w-4" /> Post new job
                </button>
            </div>
            {jobs.length === 0 ? (
                <div className="brand-card p-10 text-center text-sm text-[#6B7280]">
                    No jobs posted yet.
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2">
                    {jobs.map((j) => (
                        <div key={j.id} className="brand-card p-5" data-testid={`admin-job-card-${j.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-bold text-[#111827] truncate">{j.title}</div>
                                    <div className="text-xs text-[#6B7280]">{j.company} · {j.location}</div>
                                </div>
                                <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    j.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-[#6B7280]'
                                }`}>
                                    {j.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                                <span className="brand-chip">{j.job_type}</span>
                                <span className="brand-chip">{j.experience_level}</span>
                                <span className="brand-chip">{j.course_tag}</span>
                                <span className="brand-chip text-emerald-700 border-emerald-200 bg-emerald-50">{j.salary_range}</span>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-xs text-[#6B7280]">
                                <span>{countByJob(j.id)} applications</span>
                                <div className="flex gap-1">
                                    <button data-testid={`edit-job-${j.id}`} onClick={() => onEdit(j)} className="brand-btn-ghost px-2"><Edit2 className="h-3.5 w-3.5" /></button>
                                    <button data-testid={`delete-job-${j.id}`} onClick={() => onDelete(j)} className="brand-btn-ghost px-2 hover:text-[#E52C2C]"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ApplicationsTab({ applications, reload }) {
    const [expanded, setExpanded] = useState(null);

    const updateStatus = async (a, status) => {
        try {
            await api.put(`/admin/applications/${a.id}/status`, { status });
            reload();
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); } // eslint-disable-line no-alert
    };

    const downloadResume = async (a) => {
        try {
            const res = await api.get(`/admin/applications/${a.id}`);
            const full = res.data;
            const bin = atob(full.resume_base64);
            const arr = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
            const blob = new Blob([arr], { type: full.resume_mimetype || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = full.resume_filename || `resume-${a.id}`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) { alert('Failed to download'); } // eslint-disable-line no-alert
    };

    if (applications.length === 0) {
        return <div className="brand-card p-10 text-center text-sm text-[#6B7280]">No applications yet.</div>;
    }

    return (
        <div className="space-y-3">
            {applications.map((a) => (
                <div key={a.id} className="brand-card overflow-hidden" data-testid={`app-row-${a.id}`}>
                    <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#1C3A94] to-[#3552B8] text-sm font-bold text-white">
                            {(a.full_name || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-[#111827] truncate">{a.full_name}</div>
                            <div className="text-xs text-[#6B7280] truncate">
                                {a.job_title} @ {a.job_company} · {a.learner_id}
                            </div>
                        </div>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[a.status]}`}>
                            {a.status}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-[#9CA3AF] transition ${expanded === a.id ? 'rotate-180' : ''}`} />
                    </div>
                    {expanded === a.id && (
                        <div className="border-t border-[#E5E7EB] p-4 bg-[#F9FAFB] text-xs">
                            <div className="grid gap-3 md:grid-cols-2">
                                <div><span className="font-semibold">Email:</span> {a.email}</div>
                                <div><span className="font-semibold">Contact:</span> {a.contact_number}</div>
                                <div>
                                    <span className="font-semibold">LinkedIn:</span>{' '}
                                    <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="text-[#1C3A94] underline">
                                        {a.linkedin_url}
                                    </a>
                                </div>
                                <div><span className="font-semibold">Submitted:</span> {new Date(a.submitted_at).toLocaleString()}</div>
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <button
                                    data-testid={`download-resume-${a.id}`}
                                    onClick={() => downloadResume(a)}
                                    className="brand-btn-outline gap-1.5 text-xs py-1.5"
                                >
                                    <Download className="h-3.5 w-3.5" /> Download resume
                                </button>
                                <div className="flex items-center gap-1 ml-auto">
                                    <span className="text-[10px] uppercase font-semibold text-[#9CA3AF] mr-2">Update status:</span>
                                    {STATUS_ORDER.map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`status-${a.id}-${s}`}
                                            onClick={() => updateStatus(a, s)}
                                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                                a.status === s
                                                    ? STATUS_STYLE[s]
                                                    : 'bg-white text-[#6B7280] border border-[#E5E7EB] hover:bg-gray-50'
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function JobFormModal({ initial, title, onClose, onSubmit }) {
    const [form, setForm] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr('');
        try {
            const payload = {
                ...form,
                requirements: (form.requirements || '').split('\n').map((s) => s.trim()).filter(Boolean),
                skills_required: (form.skills_required || '').split(',').map((s) => s.trim()).filter(Boolean),
            };
            await onSubmit(payload);
        } catch (e2) {
            setErr(e2.response?.data?.detail || 'Failed');
        } finally { setBusy(false); }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
        >
            <motion.form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                className="w-full max-w-3xl brand-card p-6 md:p-8 max-h-[90vh] overflow-y-auto my-auto"
                data-testid="job-form-modal"
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 className="text-lg font-bold text-[#111827]">{title}</h2>
                    <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]"><X className="h-5 w-5" /></button>
                </div>

                {err && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                    <F label="Title *"><input data-testid="jf-title" required className="brand-input" value={form.title} onChange={set('title')} /></F>
                    <F label="Company *"><input data-testid="jf-company" required className="brand-input" value={form.company} onChange={set('company')} /></F>
                    <F label="Location *"><input data-testid="jf-location" required className="brand-input" value={form.location} onChange={set('location')} /></F>
                    <F label="Salary Range *"><input data-testid="jf-salary" required className="brand-input" placeholder="₹6-10 LPA" value={form.salary_range} onChange={set('salary_range')} /></F>
                    <F label="Job Type">
                        <select className="brand-input" value={form.job_type} onChange={set('job_type')}>
                            <option>Full-time</option><option>Part-time</option><option>Internship</option><option>Contract</option>
                        </select>
                    </F>
                    <F label="Experience Level">
                        <select className="brand-input" value={form.experience_level} onChange={set('experience_level')}>
                            <option>Entry</option><option>Mid</option><option>Senior</option>
                        </select>
                    </F>
                    <F label="Course Tag">
                        <select className="brand-input" value={form.course_tag} onChange={set('course_tag')}>
                            <option>All</option><option>Data Analytics</option><option>Business Analytics</option><option>Data Science</option>
                        </select>
                    </F>
                    <F label="Application Deadline">
                        <input type="date" className="brand-input" value={(form.application_deadline || '').slice(0, 10)} onChange={set('application_deadline')} />
                    </F>
                    <F label="Description *" full>
                        <textarea data-testid="jf-description" required rows={4} className="brand-input" value={form.description} onChange={set('description')} />
                    </F>
                    <F label="Requirements (one per line)" full>
                        <textarea rows={3} className="brand-input" value={form.requirements} onChange={set('requirements')} />
                    </F>
                    <F label="Skills (comma-separated)" full>
                        <input className="brand-input" value={form.skills_required} onChange={set('skills_required')} />
                    </F>
                    <F label="Apply Link (optional)">
                        <input className="brand-input" placeholder="https://..." value={form.apply_link || ''} onChange={set('apply_link')} />
                    </F>
                    <F label="Company Logo URL (optional)">
                        <input className="brand-input" placeholder="https://..." value={form.company_logo || ''} onChange={set('company_logo')} />
                    </F>
                    <div className="md:col-span-2 flex items-center gap-2">
                        <input
                            id="isactive"
                            data-testid="jf-active"
                            type="checkbox"
                            checked={!!form.is_active}
                            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        />
                        <label htmlFor="isactive" className="text-sm text-[#111827]">Active (visible to learners)</label>
                    </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="brand-btn-outline">Cancel</button>
                    <button data-testid="jf-submit" type="submit" disabled={busy} className="brand-btn-primary">
                        {busy ? 'Saving…' : 'Save job'}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}

function F({ label, children, full }) {
    return (
        <div className={full ? 'md:col-span-2' : ''}>
            <label className="mb-1 block text-xs font-semibold text-[#111827]">{label}</label>
            {children}
        </div>
    );
}
